import React, { Component } from 'react'
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table'
import { Box, Typography, TablePagination, IconButton } from '@mui/material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import Tooltip from '@mui/material/Tooltip'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import Checkbox from '@mui/material/Checkbox'

import Button, { COLORS } from './material/Button'
import { radius } from '../theme'

const DEFAULTS = {
  PAGE: 0,
  ROWS_PER_PAGE: 10,
}

export const CELL_TYPES = {
  NOT_FOUND: 'NOT_FOUND',
  BUTTON: 'BUTTON',
  BUTTON_ARR: 'BUTTON_ARR',
  BUTTON_ICON_ARR: 'BUTTON_ICON_ARR',
  LINK: 'LINK',
  TEXT: 'TEXT',
  TEXT_CENTER: 'TEXT_CENTER',
  HOLLOW_BUTTON: 'HOLLOW_BUTTON',
  ICON_BUTTON: 'ICON_BUTTON',
  CHECKBOX: 'CHECKBOX',
  FIELD: 'FIELD',
  FLOAT_BUTTONS: 'FLOAT_BUTTONS',
}

export default class ResponsiveTableWithoutInternalPagination extends Component {
  constructor(props) {
    super(props)
    this.state = {
      activeItems: [],
      rowsPerPage: this.props.rowsPerPage || DEFAULTS.ROWS_PER_PAGE,
      hideFirstColumn: false
    }

    this.renderCell = this.renderCell.bind(this)
  }

  async exportData(data) {
    const str = JSON.stringify(data)
    const bytes = new TextEncoder().encode(str)
    const blob = new Blob([bytes], {
      type: 'application/json;charset=utf-8',
    })

    const newHandle = await window.showSaveFilePicker()
    const writableStream = await newHandle.createWritable()
    await writableStream.write(blob)
    await writableStream.close()
  }

  toggleVisibility(index) {
    const activeItems = this.state.activeItems.some((i) => i === index)
      ? this.state.activeItems.filter((i) => i !== index)
      : [...this.state.activeItems, index]

    this.setState({ activeItems })
  }

  renderCell(
    type,
    {
      label,
      onClick,
      onClickLink,
      buttons,
      icon,
      field,
      useGridSystem,
      checked,
      disabled,
    },
    columnName,
    index,
    tdIndex
  ) {
    const key = `${columnName}_${index}`
    const visible = this.state.activeItems.includes(index) ? 'visible' : ''
    const params = {
      key,
      onClick,
      onClickLink,
      label,
      useGridSystem,
      checked,
      disabled,
      buttons,
      icon,
      field,
      index,
      visible,
      tdIndex,
    }

    const typesMap = new Map([
      [CELL_TYPES.NOT_FOUND, 'getNotFoundCell'],
      [CELL_TYPES.BUTTON, 'getButtonCell'],
      [CELL_TYPES.LINK, 'getLinkCell'],
      [CELL_TYPES.BUTTON_ARR, 'getButtonArrCell'],
      [CELL_TYPES.ICON_BUTTON, 'getIconButtonCell'],
      [CELL_TYPES.CHECKBOX, 'getCheckboxCell'],
      [CELL_TYPES.HOLLOW_BUTTON, 'getHollowButtonCell'],
      [CELL_TYPES.FIELD, 'getFieldCell'],
      [CELL_TYPES.TEXT_CENTER, 'getTextCenterCell'],
      [CELL_TYPES.FLOAT_BUTTONS, 'getFloatButtonsCell'],
    ])

    return !!typesMap.get(type)
      ? this[typesMap.get(type)](params)
      : this.getDefaultCell(params)
  }

  getNotFoundCell() {
    return (
      <Td colSpan={this.props.columns.length} align='center'>
        <Typography variant='h6'>
          {this.props.noDataMessage || 'Nenhum resultado'}
        </Typography>
      </Td>
    )
  }

  getButtonCell({ key, useGridSystem, onClick, label, disabled, visible }) {
    return (
      <Td key={key} align='right' className={`td-btn ${visible}`}>
        <Button
          style={useGridSystem ? { width: '100%' } : undefined}
          small
          onClick={onClick}
          label={label}
          disabled={disabled}
        />
      </Td>
    )
  }

  getLinkCell({ key, onClick, label, index, tdIndex, visible }) {
    return (
      <Td
        key={key}
        className={`td-link ${visible}`}
        style={{
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationColor: 'var(--palette-primary-main, #EA5D0D)',
          width: this.props.columnWidth,
        }}
      >
        <span onClick={onClick}>{label}</span>
        {tdIndex === 0 && (
          <span
            className='collapse-icon'
            onClick={() => this.toggleVisibility(index)}
          >
            <KeyboardArrowUpIcon />
          </span>
        )}
      </Td>
    )
  }

  getButtonArrCell({ key, buttons, visible }) {
    let lastChild = buttons.filter((b) => b.icon == null).length
    return (
      <Td key={key} align='right' className={`td-btn-arr ${visible}`}>
        {buttons.map((button, bIndex) =>
          button.icon ? (
            <Tooltip
              key={`${key}_${bIndex}`}
              title={button.disabled ? 'Desabilitado' : button.label}
              arrow
              placement='top'
            >
              <IconButton
                disabled={!!button.disabled}
                onClick={button.onClick}
                className='td-btn-arr-icon'
                size="large">
                {button.icon}
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              key={`${key}_${bIndex}`}
              small
              style={{
                borderRadius: radius.md,
                height: '32px',
                padding: '10px',
                margin: '5px',
                background:
                  !button.disabled &&
                    button.label !== 'Ressinc' &&
                    !(bIndex + 1 === lastChild)
                    ? 'white'
                    : 'null',
                color:
                  !button.disabled &&
                    button.label !== 'Ressinc' &&
                    !(bIndex + 1 === lastChild)
                    ? 'var(--palette-primary-main, #EA5D0D)'
                    : 'white',
                border:
                  !button.disabled &&
                    button.label !== 'Ressinc' &&
                    !(bIndex + 1 === lastChild)
                    ? '1px solid var(--palette-primary-main, #EA5D0D)'
                    : 'null',
              }}
              onClick={button.onClick}
              label={button.label}
              disabled={button.disabled}
              className='td-btn-arr-btn'
            />
          )
        )}
      </Td>
    );
  }

  getFloatButtonsCell({ key, buttons, visible }) {
    let lastChild = buttons.filter((b) => b.icon == null).length

    return (
      <Td key={key} align='right' className={`td-float-buttons ${visible}`}>
        <label htmlFor={'action_' + key}>
          <Box
            component='span'
            className='td-float-buttons-trigger'
            sx={{
              padding: '5px 8px',
              borderRadius: `${radius.md}`,
              border: '1px solid',
              borderColor: 'primary.main',
              color: 'primary.main',
            }}
          >
            Manejos
          </Box>
          <input type='checkbox' id={'action_' + key} />
          <div>
            {buttons
              .filter(
                (i) =>
                  i.label !== 'Excluir' &&
                  i.label !== 'Editar' &&
                  i.label !== 'Sincronizar' &&
                  i.label !== 'Voltar para Ressinc anterior'
              )
              .map((button, bIndex) =>
                this.renderFloatingIcons(key, bIndex, button, lastChild)
              )}
          </div>
        </label>

        {buttons
          .filter(
            (i) =>
              i.label === 'Excluir' ||
              i.label === 'Editar' ||
              i.label === 'Sincronizar' ||
              i.label === 'Voltar para Ressinc anterior'
          )
          .map((button, bIndex) => this.renderActionIcons(key, bIndex, button))}
      </Td>
    )
  }

  getIconButtonCell({ key, onClick, label, icon, visible, disabled }) {
    return (
      <Td key={`${key}`} align='right' className={`td-icon-btn ${visible}`}>
        <Tooltip title={label} arrow placement='top'>
          {/* MUI v5+: <span> wrapper para que Tooltip receba eventos
              mesmo quando IconButton está disabled. */}
          <span>
            <IconButton key={`${key}`} disabled={disabled} onClick={onClick} size="large">
              {icon}
            </IconButton>
          </span>
        </Tooltip>
      </Td>
    );
  }

  getCheckboxCell({
    key,
    onClick,
    onClickLink,
    checked,
    disabled,
    visible,
    label,
    index,
    tdIndex,
  }) {
    return (
      <Td
        key={`${key}`}
        className={`td-link ${visible}`}
        style={{
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationColor: 'var(--palette-primary-main, #EA5D0D)',
          width: this.props.columnWidth,
        }}
      >
        <Checkbox
          onClick={onClick}
          checked={checked}
          disabled={disabled}
          color='primary'
          style={{ marginRight: '10px' }}
        />
        <span onClick={onClickLink}>{label}</span>
        {tdIndex === 0 && (
          <span
            className='collapse-icon'
            onClick={() => this.toggleVisibility(index)}
          >
            <KeyboardArrowUpIcon />
          </span>
        )}
      </Td>
    )
  }

  getHollowButtonCell({ key, onClick, icon, visible }) {
    return (
      <Td key={`${key}`} align='right' className={`td-hollow ${visible}`}>
        <IconButton onClick={onClick} size="large">{icon}</IconButton>
      </Td>
    );
  }

  getFieldCell({ key, field, index, tdIndex, visible }) {
    return (
      <Td key={`${key}`} className={`td-field ${visible}`}>
        {field}
        {tdIndex === 0 && (
          <span
            className='collapse-icon'
            onClick={() => this.toggleVisibility(index)}
          >
            <KeyboardArrowUpIcon />
          </span>
        )}
      </Td>
    )
  }

  getTextCenterCell({ key, label, index, tdIndex, visible }) {
    return (
      <Td key={`${key}`} align='center' className={`td-text-center ${visible}`}>
        {label}
        {tdIndex === 0 && (
          <span
            className='collapse-icon'
            onClick={() => this.toggleVisibility(index)}
          >
            <KeyboardArrowUpIcon />
          </span>
        )}
      </Td>
    )
  }

  getDefaultCell({ key, label, index, tdIndex, visible }) {
    return (
      <Td
        key={`${key}`}
        style={{ width: this.props.columnWidth }}
        className={`td-default ${visible}`}
      >
        {label}
        {tdIndex === 0 && (
          <span
            className='collapse-icon'
            onClick={() => this.toggleVisibility(index)}
          >
            <KeyboardArrowUpIcon />
          </span>
        )}
      </Td>
    )
  }

  renderFloatingIcons(key, bIndex, button, lastChild) {
    return (
      <Button
        key={`${key}_${bIndex}`}
        small
        style={{
          borderRadius: radius.md,
          height: '32px',
          padding: '10px',
          margin: '5px',
          background:
            !button.disabled && !(bIndex + 1 === lastChild) ? 'white' : 'null',
          color:
            !button.disabled && !(bIndex + 1 === lastChild)
              ? 'var(--palette-primary-main, #EA5D0D)'
              : 'null',
          border:
            !button.disabled && !(bIndex + 1 === lastChild)
              ? '1px solid var(--palette-primary-main, #EA5D0D)'
              : 'null',
        }}
        onClick={button.onClick}
        label={button.label}
        disabled={button.disabled}
        className='td-btn-arr-btn'
      />
    )
  }

  renderActionIcons(key, bIndex, button) {
    return button.icon ? (
      <Tooltip
        key={`${key}_${bIndex}`}
        title={button.disabled ? 'Desabilitado' : button.label}
        arrow
        placement='top'
      >
        <IconButton
          style={{ color: button.disabled && '#cecece', padding: '5px' }}
          onClick={button.disabled ? null : button.onClick}
          className='td-btn-arr-icon'
          size="large">
          {button.icon}
        </IconButton>
      </Tooltip>
    ) : (
      <Button
        key={`${key}_${bIndex}`}
        small
        onClick={button.onClick}
        label={button.label}
        disabled={button.disabled}
        className='td-btn-arr-btn'
      />
    );
  }

  renderTableItem(useGridSystem, row, i) {
    return (
      <Tr key={i}>
        {this.props.columns.map((column, tdIndex) =>
          this.renderCell(
            column.type,
            {
              label: column.label ? column.label(row, i) : '',
              onClick: column.onClick && column.onClick(row, i),
              onClickLink: column.onClickLink && column.onClickLink(row, i),
              buttons: column.buttons ? column.buttons(row, i) : [],
              icon: column.icon && column.icon(row, i),
              field: column.field && column.field(row, i),
              checked: column.checked && column.checked(row, i),
              disabled: column.disabled && column.disabled(row, i),
              useGridSystem,
            },
            column.name,
            i,
            tdIndex
          )
        )}
      </Tr>
    )
  }

  renderTopTablePagination() {
    return (
      <div className='top-table-pagination'>
        <TablePagination
          component="div"
          colSpan={this.props.columns.length}
          count={this.props.data.length}
          rowsPerPage={this.state.rowsPerPage}
          page={this.props.page}
          onPageChange={this.props.onPageChange}
          rowsPerPageOptions={[this.state.rowsPerPage]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to === -1 ? count : to} de ${count !== -1 ? count : to}`
          }
        />
      </div>
    )
  }

  renderTableFooter() {
    return (
      <tfoot>
        <tr>
          <td align='left'>
            <Button
              color={COLORS.WHITE}
              startIcon={<ArrowDownwardIcon />}
              onClick={() => this.exportData(this.props.toDataExport)}
              withGrid={12}
              style={{
                minWidth: '5px',
                padding: '2px 0px 2px 12px',
                width: '30px',
                display: 'none',
              }}
            />
          </td>
          <TablePagination
            colSpan={this.props.columns.length}
            count={this.props.data.length}
            rowsPerPage={this.state.rowsPerPage}
            page={this.props.page}
            onPageChange={this.props.onPageChange}
            rowsPerPageOptions={[this.state.rowsPerPage]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to === -1 ? count : to} de ${count !== -1 ? count : to
              }`
            }
          />
        </tr>
      </tfoot>
    )
  }

  render() {
    let useGridSystem = false

    const data = !this.props.skipPagination
      ? this.props.data.slice(
        this.props.page * this.state.rowsPerPage,
        (this.props.page + 1) * this.state.rowsPerPage
      )
      : this.props.data

    return (
      <>
        {this.renderTopTablePagination()}
        {/* Wrapper com scroll horizontal para evitar overflow em telas estreitas. */}
        <div style={{ width: '100%', overflowX: 'auto' }}>
        <Table
          className={'app-responsive-table ' + this.props.className}
          style={{
            margin: 0,
            // Bottom corners arredondados (token md) — top fica reto porque
            // o wrapper .top-table-pagination já tem o radius top.
            borderRadius: `0 0 ${radius.md}px ${radius.md}px`,
          }}
        >
          <Thead>
            <Tr>
              {this.props.columns.map((column, index) => {
                return <Th key={`${column.name}_${index}`}>{column.name}</Th>
              })}
            </Tr>
          </Thead>
          <Tbody>
            {!this.props.data.length ? (
              <Tr>{this.renderCell('NOT_FOUND', {})}</Tr>
            ) : (
              data.map((row, i) => this.renderTableItem(useGridSystem, row, i))
            )}
          </Tbody>

          {!this.props.skipPagination && this.renderTableFooter()}
        </Table>
        </div>
      </>
    )
  }
}
